import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Heart,
  ShoppingCart,
  ShieldCheck,
  RotateCcw,
  Loader2,
  Minus,
  Plus,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  BadgeCheck,
  Star,
  MessageCircle,
  Lock,
  Truck,
} from "lucide-react";
import { getProduct, listProducts } from "@/lib/adapters/products";
import { useCart } from "@/contexts/CartContext";
import { useWishlist } from "@/contexts/WishlistContext";
import ProductCard from "@/components/product/ProductCard";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const TABS = ["Product Overview", "Specifications", "What's in the box", "Documents"];

const APPLICATIONS = [
  {
    id: "care",
    title: "Care & Maintenance",
    body:
      "Clean gently with a soft, damp cloth after each use. Avoid harsh chemicals. Store in a cool, dry place away from direct sunlight.",
  },
  {
    id: "warranty",
    title: "Warranty",
    body:
      "This product comes with a 1-year manufacturer warranty covering defects in materials and workmanship. Extended warranty options are available at checkout.",
  },
  {
    id: "shipping",
    title: "Shipping & Returns",
    body:
      "Free shipping on orders above ₹999. Standard delivery in 3–5 business days. 10-day easy returns for eligible items.",
  },
];

const DEFAULT_QA = [
  {
    q: "Is this product suitable for pediatric use?",
    a: "Yes — this product is safe for use across all patient age groups when handled by a licensed clinician.",
    author: "Dyntra Support",
  },
  {
    q: "How often should I replace the tips?",
    a: "We recommend replacing consumable tips after each use or as clinically indicated by wear.",
    author: "Product Team",
  },
];

const SAVINGS_TIERS = [
  { min: 1, max: 2, save: 0, label: "Standard" },
  { min: 3, max: 5, save: 5, label: "Small Clinic" },
  { min: 6, max: 10, save: 10, label: "Growing Clinic" },
  { min: 11, max: 20, save: 15, label: "Established" },
  { min: 21, max: 999, save: 20, label: "Enterprise" },
];

const WHY_STEPS = [
  { title: "Trusted by 12,500+ clinicians", desc: "Preferred by leading dental clinics across India." },
  { title: "Clinically validated", desc: "Reviewed and validated for everyday dental practice." },
  { title: "Backed by expert support", desc: "Access to product specialists on WhatsApp & phone." },
];

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { addToCart, cart } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();

  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [variant, setVariant] = useState("Standard");
  const [tab, setTab] = useState(TABS[0]);
  const [related, setRelated] = useState([]);
  const [fbtPicks, setFbtPicks] = useState({});
  const [openApp, setOpenApp] = useState(null);
  const [qaList, setQaList] = useState(DEFAULT_QA);
  const [askOpen, setAskOpen] = useState(false);
  const [askForm, setAskForm] = useState({ name: "", q: "" });
  const [addedOpen, setAddedOpen] = useState(false);
  const [carouselIdx, setCarouselIdx] = useState(0);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setP(null);
    getProduct(id)
      .then((data) => setP(data))
      .catch(() => setP(null));
  }, [id]);

  useEffect(() => {
    if (!p) return;
    listProducts({
      limit: 8,
      category: p.category,
      brand: p.brand_slug,
    })
      .then(({ items }) => {
        const filtered = items.filter((x) => x.id !== p.id);
        setRelated(filtered);
        // Default FBT companions to the first 2 items.
        setFbtPicks(
          filtered.slice(0, 2).reduce((acc, it) => ({ ...acc, [it.id]: true }), {})
        );
      })
      .catch(() => setRelated([]));
  }, [p]);

  if (!p) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-[#4B2AAD]" />
      </div>
    );
  }

  const images = p.images?.length ? p.images : [p.image].filter(Boolean);
  const wished = isInWishlist(p.id);
  const inStock = p.in_stock;
  const savingsPct =
    p.mrp > p.price ? Math.round(((p.mrp - p.price) / p.mrp) * 100) : 0;

  const tier =
    SAVINGS_TIERS.find((t) => qty >= t.min && qty <= t.max) ||
    SAVINGS_TIERS[SAVINGS_TIERS.length - 1];
  const tierPrice = Math.round(p.price * (1 - tier.save / 100));
  const tierSave = (p.price - tierPrice) * qty;

  const fbtItems = related.slice(0, 2);
  const fbtSelected = fbtItems.filter((it) => fbtPicks[it.id]);
  const bundlePrice = tierPrice * qty + fbtSelected.reduce((s, it) => s + it.price, 0);
  const bundleMrp = p.mrp * qty + fbtSelected.reduce((s, it) => s + (it.mrp || it.price), 0);
  const bundleSaving = Math.max(0, bundleMrp - bundlePrice);

  const handleAddToCart = async () => {
    if (!inStock) return;
    await addToCart(p.id, qty);
    setAddedOpen(true);
  };

  const handleBuyNow = async () => {
    if (!inStock) return;
    await addToCart(p.id, qty);
    nav("/checkout");
  };

  const submitQa = () => {
    if (!askForm.q.trim()) {
      toast.error("Please enter your question.");
      return;
    }
    setQaList([{ q: askForm.q, a: "Awaiting expert reply", author: askForm.name || "You" }, ...qaList]);
    setAskOpen(false);
    setAskForm({ name: "", q: "" });
    try {
      fetch(`/api/products/${p.id}/questions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(askForm),
      }).catch(() => { });
    } catch { }
    toast.success("Question submitted");
  };

  const visibleRelated = related.slice(carouselIdx, carouselIdx + 6);

  return (
    <main className="dyntra-container py-6" data-testid="pdp-page">
      {/* Breadcrumb */}
      <nav className="text-xs text-gray-500 mb-3">
        <Link to="/" className="hover:text-[#4B2AAD]">Home</Link>
        {" / "}
        <Link to="/shop" className="hover:text-[#4B2AAD]">Shop</Link>
        {p.category && (
          <>
            {" / "}
            <Link to={`/category/${p.category}`} className="hover:text-[#4B2AAD] capitalize">
              {p.category.replace(/-/g, " ")}
            </Link>
          </>
        )}
        {" / "}
        <span className="text-gray-900 font-semibold">{p.name}</span>
      </nav>

      {/* Above the fold: 3-column */}
      <div className="grid grid-cols-1 lg:grid-cols-[80px_1fr_320px] gap-4 lg:gap-6">
        {/* Vertical thumbs */}
        <div className="flex lg:flex-col gap-2 order-2 lg:order-1">
          {images.map((im, i) => (
            <button
              key={i}
              onClick={() => setActiveImg(i)}
              className={`h-16 w-16 rounded-xl overflow-hidden border-2 shrink-0 ${activeImg === i ? "border-[#4B2AAD]" : "border-gray-200"
                }`}
            >
              <img
                src={im || "/product-placeholder.svg"}
                alt=""
                className="w-full h-full object-contain bg-white"
              />
            </button>
          ))}
        </div>

        {/* Main image + info */}
        <div className="order-1 lg:order-2 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="rounded-2xl bg-white border border-gray-100 overflow-hidden aspect-square product-img-wrap">
              <img
                src={images[activeImg] || "/product-placeholder.svg"}
                alt={p.name}
                className="w-full h-full object-contain"
              />
            </div>
            {savingsPct > 0 && (
              <span className="inline-block mt-2 rounded-md bg-emerald-50 text-emerald-700 text-xs font-bold px-2 py-1 border border-emerald-100">
                Save {savingsPct}%
              </span>
            )}
          </div>

          <div>
            {p.brand && (
              <Link
                to={`/brand/${p.brand_slug}`}
                className="text-xs font-bold uppercase tracking-wider text-[#4B2AAD] hover:underline"
              >
                {p.brand}
              </Link>
            )}
            <h1
              data-testid="pdp-title"
              className="font-display text-2xl sm:text-3xl font-extrabold text-gray-900 mt-1"
            >
              {p.name}
            </h1>
            <div className="mt-2 flex items-center gap-2 text-sm">
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold px-2 py-0.5 border border-emerald-100">
                <BadgeCheck className="h-3 w-3" />{" "}
                {inStock ? "In stock" : "Out of stock"}
              </span>
              <span className="text-gray-500 text-xs">
                SKU {p.slug || p.id}
              </span>
              {p.rating > 0 && (
                <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                  <Star className="h-3 w-3 text-[#FFB020] fill-[#FFB020]" />
                  {p.rating.toFixed(1)} · {p.reviews} reviews
                </span>
              )}
            </div>

            <div className="mt-4 flex items-baseline gap-3 flex-wrap">
              <span className="font-display text-3xl font-extrabold text-gray-900">
                ₹{tierPrice.toLocaleString()}
              </span>
              {p.mrp > p.price && (
                <>
                  <span className="text-lg text-gray-400 line-through">
                    ₹{p.mrp.toLocaleString()}
                  </span>
                  <span className="text-xs font-bold text-emerald-600">
                    Save {savingsPct}%
                  </span>
                </>
              )}
            </div>
            <p className="text-[11px] text-gray-500 mt-1">
              Inclusive of all taxes
            </p>
            <button
              type="button"
              className="text-[11px] text-[#4B2AAD] font-semibold hover:underline mt-1 inline-flex items-center gap-1"
            >
              Or Pay EMI from ₹{Math.round(tierPrice / 12).toLocaleString()}/month
              <span aria-hidden="true">›</span>
            </button>

            <div className="mt-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Choose variant
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {["Standard", "Premium", "Bulk Pack"].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setVariant(v)}
                    data-testid={`pdp-variant-${v}`}
                    className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${variant === v
                      ? "bg-[#F3EFFC] text-[#4B2AAD] border-[#4B2AAD]"
                      : "bg-white text-gray-700 border-gray-200 hover:border-[#4B2AAD]"
                      }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity savings tier table */}
            <div className="mt-5 rounded-2xl border border-gray-100 bg-white overflow-hidden">
              <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
                <p className="text-sm font-bold text-gray-900">
                  Clinic quantity savings
                </p>
                <span className="text-[11px] text-gray-500">
                  Discount applies automatically
                </span>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[#F5F3FB] text-gray-500">
                    <th className="text-left px-4 py-2 font-semibold">Quantity</th>
                    <th className="text-right px-4 py-2 font-semibold">Extra Off</th>
                    <th className="text-right px-4 py-2 font-semibold">Price/unit</th>
                  </tr>
                </thead>
                <tbody>
                  {SAVINGS_TIERS.map((t) => {
                    const price = Math.round(p.price * (1 - t.save / 100));
                    const active = tier.min === t.min;
                    return (
                      <tr
                        key={t.min}
                        className={`border-t border-gray-100 ${active ? "bg-[#F3EFFC]" : ""
                          }`}
                      >
                        <td className="px-4 py-2 font-semibold text-gray-900">
                          {t.max >= 999 ? `${t.min}+` : `${t.min}–${t.max}`}
                          <span className="ml-2 text-[10px] text-gray-500">
                            {t.label}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right text-[#4B2AAD] font-bold">
                          {t.save === 0 ? "—" : `${t.save}% off`}
                        </td>
                        <td className="px-4 py-2 text-right font-bold text-gray-900">
                          ₹{price.toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Sticky buy-box */}
        <aside className="order-3 lg:sticky lg:top-24 self-start">
          <div className="rounded-2xl border border-gray-100 bg-white p-4 space-y-3">
            <p className="text-xs text-gray-500">
              Delivering to <span className="font-semibold text-gray-800">560001</span>
              <button className="ml-1 text-[#4B2AAD] font-semibold hover:underline">
                Change
              </button>
            </p>
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase text-gray-500">
                Quantity
              </span>
              <div className="inline-flex items-center rounded-xl border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setQty((n) => Math.max(1, n - 1))}
                  data-testid="pdp-qty-minus"
                  className="px-2 py-1 hover:bg-gray-50"
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span data-testid="pdp-qty" className="w-10 text-center text-sm font-bold">
                  {qty}
                </span>
                <button
                  onClick={() => setQty((n) => n + 1)}
                  data-testid="pdp-qty-plus"
                  className="px-2 py-1 hover:bg-gray-50"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <div className="border-t border-gray-100 pt-2 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Unit price</span>
                <span className="font-semibold">₹{tierPrice.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total ({qty} qty)</span>
                <span className="font-display text-lg font-extrabold">
                  ₹{(tierPrice * qty).toLocaleString()}
                </span>
              </div>
              {tierSave > 0 && (
                <p className="text-[11px] text-emerald-600 font-semibold">
                  You save ₹{tierSave.toLocaleString()} at this quantity
                </p>
              )}
            </div>

            <button
              onClick={handleAddToCart}
              disabled={!inStock}
              data-testid="pdp-add-to-cart"
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#4B2AAD] hover:bg-[#361D82] text-white text-sm font-bold py-3 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              <ShoppingCart className="h-4 w-4" />
              {inStock ? "Add to Cart" : "Out of Stock"}
            </button>
            <button
              onClick={handleBuyNow}
              disabled={!inStock}
              data-testid="pdp-buy-now"
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#FFB020] hover:bg-[#e39d15] text-[#111827] text-sm font-bold py-3 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Buy Now →
            </button>
            <button
              onClick={() => toggleWishlist(p)}
              data-testid="pdp-wishlist"
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white hover:border-[#F43F5E] hover:text-[#F43F5E] text-sm font-semibold py-2"
            >
              <Heart className={`h-4 w-4 ${wished ? "text-[#F43F5E] fill-[#F43F5E]" : ""}`} />
              {wished ? "In Wishlist" : "Add to Wishlist"}
            </button>

            {inStock && p.stock && p.stock <= 10 && (
              <p
                data-testid="pdp-low-stock"
                className="text-[11px] font-semibold text-emerald-600 text-center"
              >
                Only {p.stock} left at this price
              </p>
            )}

            <div className="rounded-xl bg-[#F3EFFC] border border-[#4B2AAD]/15 p-3 text-[11px] text-gray-700 flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 text-[#4B2AAD] shrink-0 mt-0.5" />
              <span>
                Add your <b>Dental Council Registration ID</b> at checkout to unlock
                clinical pricing on select products.
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2 pt-3 border-t border-gray-100 text-[11px]">
              <TrustPill icon={BadgeCheck} label="100% authentic" />
              <TrustPill icon={RotateCcw} label="10-day return" />
              <TrustPill icon={ShieldCheck} label="1-year warranty" />
              <TrustPill icon={Lock} label="Secure payments" />
            </div>
          </div>
        </aside>
      </div>

      {/* Tabs */}
      <section className="mt-8 rounded-2xl border border-gray-100 bg-white overflow-hidden">
        <div className="flex border-b border-gray-100 overflow-x-auto no-scrollbar">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`pdp-tab-${t.replace(/\s+/g, "-").toLowerCase()}`}
              className={`px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px ${tab === t
                ? "border-[#4B2AAD] text-[#4B2AAD]"
                : "border-transparent text-gray-500 hover:text-gray-800"
                }`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="p-5 text-sm text-gray-700">
          {tab === "Product Overview" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <p className="leading-relaxed">
                {p.description ||
                  "This product is trusted by leading dental clinics for daily use. Designed with clinical excellence and durability in mind, it offers precise performance across a wide range of dental applications."}
              </p>
              <ul className="space-y-2">
                {(p.features || [
                  "Precision-engineered for dental practice",
                  "Ergonomic design reduces hand fatigue",
                  "Durable stainless steel construction",
                  "Autoclave safe for easy sterilisation",
                ]).map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[#4B2AAD] mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {tab === "Specifications" && (
            <table className="w-full">
              <tbody>
                {Object.entries(
                  p.specifications || {
                    Brand: p.brand || "Dyntra",
                    Category: p.category || "General",
                    Warranty: "1 year",
                    Origin: "India",
                  }
                ).map(([k, v]) => (
                  <tr key={k} className="border-b border-gray-100 last:border-0">
                    <td className="py-2 pr-3 text-gray-500 w-1/3">{k}</td>
                    <td className="py-2 font-semibold text-gray-900">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {tab === "What's in the box" && (
            <ul className="space-y-2 list-disc pl-5">
              <li>1 × {p.name}</li>
              <li>User Manual</li>
              <li>Warranty Card</li>
              <li>Care instructions</li>
            </ul>
          )}
          {tab === "Documents" && (
            <ul className="space-y-2">
              <li>
                <a href="#" className="text-[#4B2AAD] font-semibold hover:underline">
                  Product Datasheet.pdf
                </a>
              </li>
              <li>
                <a href="#" className="text-[#4B2AAD] font-semibold hover:underline">
                  Instructions for Use.pdf
                </a>
              </li>
              <li>
                <a href="#" className="text-[#4B2AAD] font-semibold hover:underline">
                  Compliance Certificate.pdf
                </a>
              </li>
            </ul>
          )}
        </div>
      </section>

      {/* Frequently Bought Together */}
      {fbtItems.length > 0 && (
        <section className="mt-8 rounded-2xl border border-gray-100 bg-white p-5">
          <h3 className="font-display text-base font-bold text-gray-900 mb-3">
            Frequently bought together
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <FBTCard
                item={{ ...p, price: tierPrice }}
                checked
                disabled
              />
              {fbtItems.map((it) => (
                <FBTCard
                  key={it.id}
                  item={it}
                  checked={!!fbtPicks[it.id]}
                  onToggle={() =>
                    setFbtPicks((pk) => ({ ...pk, [it.id]: !pk[it.id] }))
                  }
                />
              ))}
            </div>
            <div className="rounded-2xl bg-[#F5F3FB] p-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                Bundle price
              </p>
              <p className="font-display text-2xl font-extrabold text-[#4B2AAD] mt-1">
                ₹{bundlePrice.toLocaleString()}
              </p>
              {bundleSaving > 0 && (
                <p className="text-xs text-emerald-600 font-semibold">
                  Save ₹{bundleSaving.toLocaleString()} with bundle
                </p>
              )}
              <button
                onClick={async () => {
                  await addToCart(p.id, qty);
                  for (const it of fbtSelected) {
                    await addToCart(it.id, 1);
                  }
                  setAddedOpen(true);
                }}
                className="mt-3 w-full dyntra-btn-primary text-sm py-2.5"
              >
                <ShoppingCart className="h-4 w-4" />
                Add {1 + fbtSelected.length} items to Cart
              </button>
              <p className="text-[11px] text-gray-500 mt-2">
                Bundle includes this product and your selected companions.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Why clinics choose */}
      <section className="mt-8 rounded-2xl border border-gray-100 bg-white p-5 grid grid-cols-1 lg:grid-cols-[1fr_1.5fr] gap-5 items-center">
        <div className="h-48 rounded-2xl bg-gradient-to-br from-[#4B2AAD] to-[#6B4CD9] flex items-center justify-center text-white">
          <div className="text-center px-4">
            <BadgeCheck className="h-10 w-10 mx-auto opacity-90" />
            <p className="mt-2 font-display text-lg font-bold">
              Trusted by leading clinics
            </p>
          </div>
        </div>
        <div>
          <h3 className="font-display text-lg font-bold text-gray-900">
            Why clinics choose {p.brand || "Dyntra"}
          </h3>
          <ol className="mt-3 space-y-3">
            {WHY_STEPS.map((s, i) => (
              <li key={s.title} className="flex items-start gap-3">
                <span className="h-6 w-6 rounded-full bg-[#F3EFFC] text-[#4B2AAD] flex items-center justify-center text-xs font-bold shrink-0">
                  {i + 1}
                </span>
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {s.title}
                  </p>
                  <p className="text-xs text-gray-500">{s.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Clinical applications accordion */}
      <section className="mt-8 rounded-2xl border border-gray-100 bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100">
          <h3 className="font-display text-base font-bold text-gray-900">
            Clinical applications &amp; policies
          </h3>
        </div>
        {APPLICATIONS.map((a) => (
          <div key={a.id} className="border-b border-gray-100 last:border-0">
            <button
              onClick={() => setOpenApp(openApp === a.id ? null : a.id)}
              className="w-full px-5 py-3 text-left flex items-center justify-between"
            >
              <span className="text-sm font-semibold text-gray-900">
                {a.title}
              </span>
              {openApp === a.id ? (
                <ChevronUp className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              )}
            </button>
            {openApp === a.id && (
              <p className="px-5 pb-4 text-xs text-gray-600 leading-relaxed">
                {a.body}
              </p>
            )}
          </div>
        ))}
      </section>

      {/* Product Q&A */}
      <section className="mt-8 rounded-2xl border border-gray-100 bg-white p-5">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-base font-bold text-gray-900">
            Product Q&amp;A
          </h3>
          <button
            onClick={() => setAskOpen(true)}
            data-testid="pdp-ask-question"
            className="dyntra-btn-primary text-xs py-1.5"
          >
            <MessageCircle className="h-4 w-4" /> Ask a Question
          </button>
        </div>
        <ul className="mt-3 space-y-3">
          {qaList.map((qa, i) => (
            <li key={i} className="rounded-2xl bg-[#F5F3FB] p-3">
              <p className="text-sm font-bold text-gray-900">Q: {qa.q}</p>
              <p className="text-xs text-gray-600 mt-1">A: {qa.a}</p>
              <p className="text-[10px] text-gray-400 mt-1">— {qa.author}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* You may also like */}
      {related.length > 0 && (
        <section className="mt-8 rounded-2xl border border-gray-100 bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-base font-bold text-gray-900">
              You may also like
            </h3>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCarouselIdx((i) => Math.max(0, i - 1))}
                className="h-8 w-8 rounded-full border border-gray-200 flex items-center justify-center hover:border-[#4B2AAD] hover:text-[#4B2AAD]"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() =>
                  setCarouselIdx((i) =>
                    Math.min(Math.max(0, related.length - 6), i + 1)
                  )
                }
                className="h-8 w-8 rounded-full border border-gray-200 flex items-center justify-center hover:border-[#4B2AAD] hover:text-[#4B2AAD]"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {visibleRelated.map((rp) => (
              <ProductCard key={rp.id} product={rp} />
            ))}
          </div>
        </section>
      )}

      {/* Footer USP strip */}
      <div className="mt-6 rounded-2xl bg-white border border-gray-100 p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <UspItem icon={ShieldCheck} title="100% Genuine Products" sub="Trusted by Dental Professionals" />
        <UspItem icon={Truck} title="Fast & Reliable Delivery" sub="Across India" />
        <UspItem icon={RotateCcw} title="Easy Returns" sub="7-Day Return Policy" />
        <UspItem icon={Lock} title="Secure Payments" sub="100% Protected Transactions" />
      </div>

      {/* Ask a Question modal */}
      <Dialog open={askOpen} onOpenChange={setAskOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">Ask a Question</DialogTitle>
            <DialogDescription>
              Get expert insights about this product from our specialists.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                Your name (optional)
              </label>
              <input
                value={askForm.name}
                onChange={(e) => setAskForm((f) => ({ ...f, name: e.target.value }))}
                className="mt-1 w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-3 py-2 outline-none text-sm"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                Your question
              </label>
              <textarea
                value={askForm.q}
                onChange={(e) => setAskForm((f) => ({ ...f, q: e.target.value }))}
                rows={4}
                className="mt-1 w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-3 py-2 outline-none text-sm resize-none"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button className="dyntra-btn-ghost" onClick={() => setAskOpen(false)}>
                Cancel
              </button>
              <button className="dyntra-btn-primary" onClick={submitQa}>
                Submit Question
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Added-to-Cart Modal */}
      <Dialog open={addedOpen} onOpenChange={setAddedOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader className="items-center text-center">
            <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            </div>
            <DialogTitle className="font-display mt-2">
              Added to cart
            </DialogTitle>
            <DialogDescription>
              {qty} × {p.name} has been added to your cart.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-2xl bg-[#F5F3FB] p-3 flex gap-3 items-center">
            <img
              src={p.image || "/product-placeholder.svg"}
              alt=""
              className="h-14 w-14 rounded-xl object-contain bg-white"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-900 line-clamp-1">
                {p.name}
              </p>
              <p className="text-xs text-gray-500">
                Qty {qty} × ₹{tierPrice.toLocaleString()}
              </p>
            </div>
            <p className="text-sm font-bold text-gray-900">
              ₹{(tierPrice * qty).toLocaleString()}
            </p>
          </div>

          <div className="rounded-2xl border border-gray-100 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Cart subtotal</span>
              <span className="font-bold text-gray-900">
                ₹{(cart.subtotal + tierPrice * qty).toLocaleString()}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              className="dyntra-btn-ghost text-xs"
              onClick={() => setAddedOpen(false)}
              data-testid="added-continue"
            >
              Continue Shopping
            </button>
            <button
              className="dyntra-btn-ghost text-xs"
              onClick={() => nav("/cart")}
              data-testid="added-view-cart"
            >
              View Cart
            </button>
            <button
              className="col-span-2 dyntra-btn-primary text-xs"
              onClick={() => nav("/checkout")}
              data-testid="added-checkout"
            >
              Proceed to Checkout →
            </button>
          </div>

          <p className="mt-2 text-[10px] text-gray-400 text-center inline-flex items-center justify-center gap-1">
            <Lock className="h-3 w-3" /> Secure checkout · encrypted payments
          </p>
        </DialogContent>
      </Dialog>
    </main>
  );
}

function TrustPill({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1 text-gray-600">
      <Icon className="h-3.5 w-3.5 text-[#4B2AAD]" /> {label}
    </span>
  );
}

function FBTCard({ item, checked, disabled, onToggle }) {
  return (
    <label
      className={`rounded-2xl border p-2 flex flex-col text-left ${checked ? "border-[#4B2AAD] bg-[#F3EFFC]" : "border-gray-100 bg-white"
        } ${disabled ? "opacity-90" : "cursor-pointer"}`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        disabled={disabled}
        className="mb-2 accent-[#4B2AAD]"
      />
      <div className="h-24 rounded-xl bg-[#F5F3FB] flex items-center justify-center overflow-hidden">
        <img
          src={item.image || "/product-placeholder.svg"}
          alt=""
          className="h-full w-full object-contain"
        />
      </div>
      <p className="mt-2 text-xs font-bold text-gray-900 line-clamp-2 min-h-[32px]">
        {item.name}
      </p>
      <p className="text-xs font-bold text-[#4B2AAD]">
        ₹{item.price?.toLocaleString()}
      </p>
    </label>
  );
}

function UspItem({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="h-9 w-9 rounded-full bg-[#F3EFFC] flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-[#4B2AAD]" />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-bold text-gray-900 leading-tight truncate">{title}</p>
        <p className="text-[10px] text-gray-500 leading-tight truncate">{sub}</p>
      </div>
    </div>
  );
}
